/**
 * Tooltip Component for Contextual Help
 * Provides hover and click-triggered help information
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 */

import React, { useState, useRef, useEffect } from 'react';
import { HelpCircle, X, Info } from 'lucide-react';

const Tooltip = ({
  children,
  content,
  title,
  position = 'top',
  trigger = 'hover', // 'hover', 'click', 'focus'
  variant = 'dark', // 'dark', 'light', 'info', 'warning'
  maxWidth = '300px',
  disabled = false,
  showIcon = false,
  persistent = false, // Whether tooltip stays open when clicked
  className = ""
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [actualPosition, setActualPosition] = useState(position);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  // Calculate optimal position based on viewport
  const calculatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight
    };

    let newPosition = position;

    // Check if tooltip would overflow viewport and adjust position
    switch (position) {
      case 'top':
        if (triggerRect.top - tooltipRect.height < 10) {
          newPosition = 'bottom';
        }
        break;
      case 'bottom':
        if (triggerRect.bottom + tooltipRect.height > viewport.height - 10) {
          newPosition = 'top';
        }
        break;
      case 'left':
        if (triggerRect.left - tooltipRect.width < 10) {
          newPosition = 'right';
        }
        break;
      case 'right':
        if (triggerRect.right + tooltipRect.width > viewport.width - 10) {
          newPosition = 'left';
        }
        break;
    }

    setActualPosition(newPosition);
  };

  const showTooltip = () => {
    if (disabled) return;
    setIsVisible(true);
    setTimeout(calculatePosition, 0);
  };

  const hideTooltip = () => {
    if (persistent && trigger === 'click') return;
    setIsVisible(false);
  };

  const toggleTooltip = () => {
    if (disabled) return;
    if (isVisible) {
      setIsVisible(false);
    } else {
      showTooltip();
    }
  };

  // Handle click outside to close tooltip
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        trigger === 'click' &&
        isVisible &&
        triggerRef.current &&
        tooltipRef.current &&
        !triggerRef.current.contains(event.target) &&
        !tooltipRef.current.contains(event.target)
      ) {
        setIsVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isVisible, trigger]);

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isVisible) {
        setIsVisible(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible]);

  const getVariantStyles = () => {
    switch (variant) {
      case 'light':
        return 'bg-white text-gray-800 border border-gray-200 shadow-lg';
      case 'info':
        return 'bg-blue-600 text-white border border-blue-700';
      case 'warning':
        return 'bg-yellow-500 text-white border border-yellow-600';
      default: // dark
        return 'bg-gray-900 text-white border border-gray-700';
    }
  };

  const getPositionStyles = () => {
    const base = 'absolute z-50 px-3 py-2 text-sm rounded-lg pointer-events-auto';
    
    switch (actualPosition) {
      case 'top':
        return `${base} bottom-full left-1/2 transform -translate-x-1/2 mb-2`;
      case 'bottom':
        return `${base} top-full left-1/2 transform -translate-x-1/2 mt-2`;
      case 'left':
        return `${base} right-full top-1/2 transform -translate-y-1/2 mr-2`;
      case 'right':
        return `${base} left-full top-1/2 transform -translate-y-1/2 ml-2`;
      default:
        return `${base} bottom-full left-1/2 transform -translate-x-1/2 mb-2`;
    }
  };

  const getArrowStyles = () => {
    const arrowBase = 'absolute w-2 h-2 transform rotate-45';
    const arrowColor = variant === 'light' ? 'bg-white border-gray-200' : 
                     variant === 'info' ? 'bg-blue-600 border-blue-700' :
                     variant === 'warning' ? 'bg-yellow-500 border-yellow-600' :
                     'bg-gray-900 border-gray-700';

    switch (actualPosition) {
      case 'top':
        return `${arrowBase} ${arrowColor} top-full left-1/2 -translate-x-1/2 -mt-1 border-b border-r`;
      case 'bottom':
        return `${arrowBase} ${arrowColor} bottom-full left-1/2 -translate-x-1/2 -mb-1 border-t border-l`;
      case 'left':
        return `${arrowBase} ${arrowColor} left-full top-1/2 -translate-y-1/2 -ml-1 border-t border-r`;
      case 'right':
        return `${arrowBase} ${arrowColor} right-full top-1/2 -translate-y-1/2 -mr-1 border-b border-l`;
      default:
        return `${arrowBase} ${arrowColor} top-full left-1/2 -translate-x-1/2 -mt-1 border-b border-r`;
    }
  };

  const triggerProps = {
    ref: triggerRef,
    className: `${className} ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-help'}`,
    ...(trigger === 'hover' && {
      onMouseEnter: showTooltip,
      onMouseLeave: hideTooltip
    }),
    ...(trigger === 'click' && {
      onClick: toggleTooltip
    }),
    ...(trigger === 'focus' && {
      onFocus: showTooltip,
      onBlur: hideTooltip
    })
  };

  return (
    <div className="relative inline-block">
      <div {...triggerProps}>
        {children}
        {showIcon && (
          <HelpCircle className="w-4 h-4 ml-1 inline-block text-gray-400 hover:text-gray-600 transition-colors" />
        )}
      </div>

      {isVisible && (
        <div
          ref={tooltipRef}
          className={`${getPositionStyles()} ${getVariantStyles()}`}
          style={{ maxWidth }}
          role="tooltip"
          aria-hidden={!isVisible}
        >
          {/* Arrow */}
          <div className={getArrowStyles()} />
          
          {/* Content */}
          <div className="relative">
            {title && (
              <div className="flex items-center justify-between mb-1">
                <h4 className="font-semibold text-sm">{title}</h4>
                {persistent && trigger === 'click' && (
                  <button
                    onClick={() => setIsVisible(false)}
                    className="ml-2 text-current opacity-70 hover:opacity-100 transition-opacity"
                    aria-label="Close tooltip"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            )}
            
            {typeof content === 'string' ? (
              <p className="text-sm leading-relaxed">{content}</p>
            ) : (
              content
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Convenience wrapper for info tooltips
export const InfoTooltip = ({ content, title, ...props }) => (
  <Tooltip
    content={content}
    title={title}
    variant="info"
    trigger="hover"
    showIcon
    {...props}
  >
    <Info className="w-4 h-4 text-blue-500 hover:text-blue-600 transition-colors" />
  </Tooltip>
);

// Convenience wrapper for help tooltips
export const HelpTooltip = ({ content, title, ...props }) => (
  <Tooltip
    content={content}
    title={title}
    variant="dark"
    trigger="click"
    persistent
    showIcon
    {...props}
  >
    <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors" />
  </Tooltip>
);

export default Tooltip;